import { useEffect, useMemo, useRef, useState } from 'react';
import Digits from './Digits';
import CountdownBox from './CountdownBox';
import cx from '@utils/cx.util';

type CountdownProps = {
  targetDate?: Date | string | number;
  className?: string;
  countdownBoxClassName?: string;
  labelClassName?: string;
  digitClassName?: string;
  digitContainerClassName?: string;
};

type TimeLeft = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

function getTimeLeft(target: number): TimeLeft {
  const now = Date.now();
  const diffMs = Math.max(0, target - now);
  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / (24 * 3600));
  const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds };
}

const Countdown = ({
  targetDate,
  className,
  countdownBoxClassName,
  labelClassName,
  digitClassName,
  digitContainerClassName,
}: CountdownProps) => {
  // Fake data (10:24:59 from now) if no target is provided
  const defaultTarget = useMemo(() => Date.now() + (3600 + 10 * 3600 + 24 * 60 + 59) * 1000, []);
  const targetMs = useMemo(() => {
    if (!targetDate) return defaultTarget;
    if (targetDate instanceof Date) return targetDate.getTime();
    const asNum = typeof targetDate === 'string' ? Date.parse(targetDate) : targetDate;
    return Number.isFinite(asNum) ? asNum : defaultTarget;
  }, [defaultTarget, targetDate]);

  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => getTimeLeft(targetMs));
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    setTimeLeft(getTimeLeft(targetMs));
    if (intervalRef.current) window.clearInterval(intervalRef.current);
    intervalRef.current = window.setInterval(() => {
      setTimeLeft(getTimeLeft(targetMs));
    }, 1000);
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [targetMs]);

  const { days, hours, minutes, seconds } = timeLeft;

  return (
    <div
      className={cx(
        'tw:grid tw:auto-cols-max tw:grid-flow-col tw:justify-center tw:gap-2 tw:text-center',
        className
      )}>
      <CountdownBox
        value={days}
        label='days'
        hideIfZero
        className={countdownBoxClassName}
        labelClassName={labelClassName}
        digitClassName={digitClassName}
        digitContainerClassName={digitContainerClassName}
      />
      <CountdownBox
        value={hours}
        label='hours'
        className={countdownBoxClassName}
        labelClassName={labelClassName}
        digitClassName={digitClassName}
        digitContainerClassName={digitContainerClassName}
      />
      <CountdownBox
        value={minutes}
        label='minutes'
        className={countdownBoxClassName}
        labelClassName={labelClassName}
        digitClassName={digitClassName}
        digitContainerClassName={digitContainerClassName}
      />
      <CountdownBox
        value={seconds}
        label='seconds'
        className={countdownBoxClassName}
        labelClassName={labelClassName}
        digitClassName={digitClassName}
        digitContainerClassName={digitContainerClassName}
      />
    </div>
  );
};

export default Countdown;
